import React, { useEffect, useMemo, useState } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import {
  Row,
  Col,
  Card,
  Badge,
  Button,
  Form,
  Spinner,
  Pagination,
} from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import PageTitle from "../../layouts/PageTitle";
import api, { getFileUrl } from "../../../api";
import EmailModal from "../Common/EmailModal";
import PermissionWrapper from "../PermissionWrapper";
import usePermission from "../../../hooks/usePermission";

const Billing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useSelector((state) => state.auth?.userDetails || {});
  const { hasPermission, isAdmin } = usePermission();

  const [billings, setBillings] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);

  const [statusFilter, setStatusFilter] = useState("All");
  const [projectFilter, setProjectFilter] = usePersistentState("billing_cards_project", searchParams.get("project") || "");
  const [workOrderFilter, setWorkOrderFilter] = usePersistentState("billing_cards_wo", searchParams.get("workOrder") || "");
  const [search, setSearch] = usePersistentState("billing_cards_search", "");

  const PAGE_SIZE = 9;
  const [currentPage, setCurrentPage] = usePersistentState("billing_cards_page", 1);

  const [loading, setLoading] = useState(true);

  // Email States
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailInitialData, setEmailInitialData] = useState({});

  const canSendEmail = isAdmin || hasPermission("send:email");
  const canAddBilling = isAdmin || hasPermission("create:billings");

  /* =============================
  FETCH DATA
  ============================= */
  useEffect(() => {
    const load = async () => {
      try {
        const [woRes, billingRes] = await Promise.all([
          api.get("/work-orders?limit=1000"),
          api.get("/billing"),
        ]);

        if (woRes.data.success) setWorkOrders(woRes.data.data || []);
        if (billingRes.data.success) setBillings(billingRes.data.data || []);
      } catch (err) {
        console.error("Billing load error:", err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const handleOpenEmail = (wo, billing = null) => {
    setEmailInitialData({
      to: wo.info?.client?.email || "",
      clientId: wo.info?.client?._id || wo.info?.client || "",
      subject: `Regarding Work Order: ${wo.info?.woNo || ""}`,
      placeholders: {
        clientName: wo.info?.client?.name || "",
        projectName: wo.info?.project?.projectName || "",
        number: wo.info?.woNo || "",
        type: "Work Order",
        invoiceNumber: billing?.billNo || "",
        amount: billing?.totalAmount || "",
        dueDate: billing?.dueDate ? new Date(billing.dueDate).toISOString().split("T")[0] : "",
      }
    });
    setShowEmailModal(true);
  };

  /* =============================
  GROUP BILLINGS BY WORK ORDER
  ============================= */
  const workOrderBilling = useMemo(() => {
    const map = {};

    billings.forEach((b) => {
      const woId =
        typeof b.workOrder === "object" ? b.workOrder._id : b.workOrder;

      if (!map[woId]) {
        map[woId] = {
          workOrder: woId,
          total: 0,
          raised: 0,
          invoices: [],
        };
      }

      const totalAmount = Number(b.totalAmount || 0);
      map[woId].total += totalAmount;
      map[woId].raised += totalAmount;

      map[woId].invoices.push(b);
    });

    return Object.values(map);
  }, [billings]);
  /* =============================
  BUILD WORK ORDER CARDS
  (SHOW EVEN WITHOUT BILLING)
 ============================= */
  const workOrderCards = useMemo(() => {
    return workOrders.map((wo) => {
      const billing = workOrderBilling.find((b) => b.workOrder === wo._id);

      const total = billing?.total || 0;
      const raised = billing?.raised || 0;

      const label =
        raised >= total && total > 0
          ? "Raised"
          : raised > 0
            ? "Partially Raised"
            : "Pending";

      return {
        workOrder: wo._id,
        info: wo,
        total,
        raised,
        label,
      };
    });
  }, [workOrders, workOrderBilling]);

  /* =============================
  FILTER DATA
  ============================= */
  const filteredWorkOrders = useMemo(() => {
    return workOrderCards.filter((wo) => {
      if (statusFilter !== "All" && statusFilter !== wo.label) return false;

      if (projectFilter) {
        const pId = wo.info?.project?._id || wo.info?.project;
        if (String(pId) !== String(projectFilter)) return false;
      }

      if (workOrderFilter && String(wo.workOrder) !== String(workOrderFilter)) return false;

      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const woNo = String(wo.info?.woNo || wo.info?.woNumber || "").toLowerCase();
        const label = String(wo.info?.label || "").toLowerCase();
        const projectName = String(wo.info?.project?.projectName || wo.info?.projectName || "").toLowerCase();
        const projectCode = String(wo.info?.project?.projectCode || "").toLowerCase();
        const clientName = String(wo.info?.client?.companyName || wo.info?.client?.name || "").toLowerCase();

        const matches =
          woNo.includes(q) ||
          label.includes(q) ||
          projectName.includes(q) ||
          projectCode.includes(q) ||
          clientName.includes(q);

        if (!matches) return false;
      }

      return true;
    });
  }, [workOrderCards, statusFilter, projectFilter, workOrderFilter, search]);

  /* =============================
  PAGINATION CALCULATION
  ============================= */
  const totalCount = filteredWorkOrders.length;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1;

  const paginatedWorkOrders = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredWorkOrders.slice(start, start + PAGE_SIZE);
  }, [filteredWorkOrders, currentPage]);

  const paginationItems = useMemo(() => {
    if (totalPages <= 1) return [];

    const delta = 2;
    const start = Math.max(2, currentPage - delta);
    const end   = Math.min(totalPages - 1, currentPage + delta);
    const items = [];

    items.push(
      <Pagination.Item key={1} active={currentPage === 1} onClick={() => setCurrentPage(1)}>
        1
      </Pagination.Item>
    );

    if (start > 2) items.push(<Pagination.Ellipsis key="s-ellipsis" disabled />);

    for (let p = start; p <= end; p++) {
      items.push(
        <Pagination.Item key={p} active={currentPage === p} onClick={() => setCurrentPage(p)}>
          {p}
        </Pagination.Item>
      );
    }

    if (end < totalPages - 1) items.push(<Pagination.Ellipsis key="e-ellipsis" disabled />);

    if (totalPages > 1) {
      items.push(
        <Pagination.Item
          key={totalPages}
          active={currentPage === totalPages}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </Pagination.Item>
      );
    }

    return items;
  }, [currentPage, totalPages]);

  /* =============================
  DERIVED PROJECT LIST
  ============================= */
  const projects = useMemo(() => {
    const map = {};
    workOrders.forEach((w) => {
      const pId = w.project?._id || (typeof w.project === "string" ? w.project : null);
      const pName = w.project?.projectName || w.projectName || w.project?.name;
      if (pId && pName) {
        map[pId] = pName;
      }
    });
    return Object.entries(map);
  }, [workOrders]);

  const availableWorkOrders = useMemo(() => {
    if (!projectFilter) return workOrders;
    return workOrders.filter((w) => {
      const pId = w.project?._id || w.project;
      return String(pId) === String(projectFilter);
    });
  }, [workOrders, projectFilter]);

  /* =============================
  HELPERS
  ============================= */
  const getWorkOrderInfo = (id) => workOrders.find((w) => w._id === id);

  const statusVariant = (paid, total) => {
    if (paid >= total && total > 0) return "success";
    if (paid > 0) return "warning";
    return "danger";
  };

  const statusLabel = (paid, total) => {
    if (paid >= total && total > 0) return "Raised";
    if (paid > 0) return "Partially Raised";
    return "Pending";
  };

  /* =============================
  UI
  ============================= */
  return (
    <>
      <PageTitle activeMenu="Billing" motherMenu="Commercial" />
      <Card className="mb-4"></Card>

      {/* ===== FILTER BAR ===== */}
      <Row className="mb-4 g-2 align-items-end">
        <Col md={3} sm={6}>
          <Form.Label className="small fw-semibold">Search</Form.Label>
          <Form.Control
            type="text"
            placeholder="Search WO, label, project, client..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
          />
        </Col>

        <Col md={3} sm={6}>
          <Form.Label className="small fw-semibold">Project</Form.Label>
          <Form.Select
            value={projectFilter}
            onChange={(e) => {
              setProjectFilter(e.target.value);
              setWorkOrderFilter("");
              setCurrentPage(1);
            }}
          >
            <option value="">All Projects</option>
            {projects.map(([pId, pName]) => (
              <option key={pId} value={pId}>
                {pName}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col md={3} sm={6}>
          <Form.Label className="small fw-semibold">Work Order</Form.Label>
          <Form.Select
            value={workOrderFilter}
            onChange={(e) => {
              setWorkOrderFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Work Orders</option>
            {availableWorkOrders.map((w) => (
              <option key={w._id} value={w._id}>
                {w.label ? `${w.label} (${w.woNo || w.woNumber})` : (w.woNo || w.woNumber || "WO")}
              </option>
            ))}
          </Form.Select>
        </Col>

        <Col md={projectFilter || workOrderFilter || statusFilter !== "All" || search ? 2 : 3} sm={6}>
          <Form.Label className="small fw-semibold">Payment Status</Form.Label>
          <Form.Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="All">All Status</option>
            <option value="Raised">Raised</option>
            <option value="Partially Raised">Partially Raised</option>
            <option value="Pending">Pending</option>
          </Form.Select>
        </Col>

        {(projectFilter || workOrderFilter || statusFilter !== "All" || search) && (
          <Col md={1} sm={6}>
            <Button
              variant="outline-secondary"
              className="btn-input-height w-100 text-nowrap shadow-sm d-flex align-items-center justify-content-center"
              style={{ borderRadius: "1rem" }}
              title="Reset Filters"
              onClick={() => {
                setProjectFilter("");
                setWorkOrderFilter("");
                setStatusFilter("All");
                setSearch("");
                setCurrentPage(1);
              }}
            >
              <i className="fas fa-undo me-1"></i> Reset
            </Button>
          </Col>
        )}
      </Row>

      {/* ===== CONTENT ===== */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner />
        </div>
      ) : (
        <>
          {totalCount > 0 && (
            <div className="text-muted small mb-3">
              Showing{" "}
              <strong>
                {(currentPage - 1) * PAGE_SIZE + 1}–
                {Math.min(currentPage * PAGE_SIZE, totalCount)}
              </strong>{" "}
              of <strong>{totalCount}</strong> work orders
            </div>
          )}

          {paginatedWorkOrders.length === 0 ? (
            <div className="text-center text-muted py-5">
              No work orders found for the selected filter.
            </div>
          ) : (
            <Row className="g-3">
              {paginatedWorkOrders.map((wo) => {
            const info = wo.info;

            const woValue = Number(info?.totalAmount || info?.value || 0);
            const pendingAmount = Math.max(woValue - Number(wo.raised || 0), 0);

            const percent = woValue
              ? Math.min(100, Math.round((wo.raised / woValue) * 100))
              : 0;

            const label = statusLabel(wo.raised, woValue);

            return (
              <Col key={wo.workOrder} md={6} xl={4}>
                <Card className="h-100 shadow-sm">
                  <Card.Body>
                    {/* HEADER */}
                    <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <h6 className="fw-bold mb-1 text-primary" style={{ lineHeight: 1.2 }}>
                          {info?.label || info?.woNo || info?.woNumber || "Work Order"}
                        </h6>
                        <div className="text-muted small mb-1" style={{ fontWeight: 600 }}>
                          <i className="fas fa-sitemap me-1 opacity-60"></i>
                          {info?.woNo || info?.woNumber || "—"}
                        </div>
                        <div className="text-dark fw-medium small mb-1">
                          <i className="fas fa-project-diagram me-1 text-muted"></i>
                          {info?.project?.projectName || info?.projectName || "—"}
                          {info?.project?.projectCode && (
                            <span className="text-muted ms-1">({info.project.projectCode})</span>
                          )}
                        </div>
                        {(info?.client?.companyName || info?.client?.name) && (
                          <div className="text-muted small mb-1">
                            <i className="fas fa-building me-1 opacity-75"></i>
                            {info.client?.companyName || info.client?.name}
                          </div>
                        )}
                      </div>

                      {/* CLIENT LOGO & STATUS */}
                      <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0">
                        {info?.client && (
                          <div
                            title={info.client?.companyName || info.client?.name || "Client"}
                            style={{
                              width: 38,
                              height: 38,
                              borderRadius: "8px",
                              border: "1px solid #e9ecef",
                              background: "#f8f9fa",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              overflow: "hidden",
                              flexShrink: 0,
                            }}
                          >
                            {info.client?.image ? (
                              <img
                                src={getFileUrl(info.client.image)}
                                alt=""
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                  padding: 2,
                                }}
                              />
                            ) : (
                              <i className="fas fa-building text-muted"></i>
                            )}
                          </div>
                        )}
                        <Badge bg={statusVariant(wo.raised, woValue)}>{label}</Badge>
                      </div>
                    </div>

                    <div className="mt-3 small">
                      <div className="d-flex justify-content-between">
                        <span>WO Value</span>
                        <strong>₹ {woValue.toLocaleString()}</strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Raised</span>
                        <strong className="text-success">
                          ₹ {wo.raised.toLocaleString()}
                        </strong>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span>Pending</span>
                        <strong className="text-danger">
                          &#8377; {pendingAmount.toLocaleString()}
                        </strong>
                      </div>
                    </div>
                    {wo.total === 0 && (
                      <small className="text-muted">
                        No billing created yet
                      </small>
                    )}

                    {/* ===== CIRCULAR PAYMENT PROGRESS ===== */}
                    <div className="d-flex justify-content-center mt-3">
                      <div
                        className="circular-progress"
                        style={{
                          "--value": percent,
                          "--progress-color":
                            label === "Raised"
                              ? "#198754"
                              : label === "Partially Raised"
                                ? "#ffc107"
                                : "#dc3545",
                        }}
                      >
                        <span>{percent}%</span>
                      </div>
                    </div>

                    {/* ===== COMMERCIAL ===== */}
                    <div className="mt-3">
                      <div className="small fw-semibold text-uppercase text-muted mb-1">
                        Commercial
                      </div>

                      <div className="d-flex gap-2 flex-wrap">
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() =>
                            navigate(`/billings?workOrder=${wo.workOrder}`)
                          }
                        >
                          Billings
                        </Button>

                        {canAddBilling && (
                          <Button
                            size="sm"
                            variant="outline-success"
                            onClick={() =>
                              navigate(`/billings/add?workOrder=${wo.workOrder}`)
                            }
                          >
                            Add Billing
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="outline-warning"
                          onClick={() =>
                            navigate(
                              `/billing/overview?workOrder=${wo.workOrder}`,
                            )
                          }
                        >
                          Overview
                        </Button>

                        <PermissionWrapper user={user} required={["send:email"]}>
                          <Button
                            size="sm"
                            variant="outline-info"
                            onClick={() => handleOpenEmail(wo)}
                          >
                            Send Email
                          </Button>
                        </PermissionWrapper>
                      </div>
                    </div>

                    {/* ===== SIGNED REPORTS & DOCUMENTS ===== */}
                    <div className="mt-3">
                      <div className="small fw-semibold text-uppercase text-muted mb-1">
                        Signed Reports & Documents
                      </div>

                      <div className="d-flex gap-2 flex-wrap">
                        {/* Signed JMR — blue */}
                        <Button
                          size="sm"
                          variant=""
                          style={{ border: "1px solid #0d6efd", color: "#0d6efd", backgroundColor: "transparent" }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#0d6efd"; e.currentTarget.style.color = "#fff"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#0d6efd"; }}
                          onClick={() => navigate(`/measurement-sheets?workOrder=${wo.workOrder}&status=Approved`)}
                        >
                          Signed JMR
                        </Button>

                        {/* Signed MIR — purple */}
                        <Button
                          size="sm"
                          variant=""
                          style={{ border: "1px solid #6f42c1", color: "#6f42c1", backgroundColor: "transparent" }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#6f42c1"; e.currentTarget.style.color = "#fff"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#6f42c1"; }}
                          onClick={() => navigate(`/mir?workOrder=${wo.workOrder}&status=Approved`)}
                        >
                          Signed MIR
                        </Button>

                        {/* Signed DC — green */}
                        <Button
                          size="sm"
                          variant=""
                          style={{ border: "1px solid #198754", color: "#198754", backgroundColor: "transparent" }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#198754"; e.currentTarget.style.color = "#fff"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#198754"; }}
                          onClick={() => navigate(`/delivery?workOrder=${wo.workOrder}&status=Approved`)}
                        >
                          Signed DC
                        </Button>

                        {/* TC/Warranty Card — cyan */}
                        <Button
                          size="sm"
                          variant=""
                          style={{ border: "1px solid #0dcaf0", color: "#0dcaf0", backgroundColor: "transparent" }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#0dcaf0"; e.currentTarget.style.color = "#000"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#0dcaf0"; }}
                          onClick={() => navigate(`/tc-documents?workOrder=${wo.workOrder}`)}
                        >
                          TC/Warranty Card
                        </Button>

                        {/* Documents — teal */}
                        <Button
                          size="sm"
                          variant=""
                          style={{ border: "1px solid #20c997", color: "#20c997", backgroundColor: "transparent" }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#20c997"; e.currentTarget.style.color = "#fff"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#20c997"; }}
                          onClick={() => navigate(`/documents?workOrder=${wo.workOrder}`)}
                        >
                          Documents
                        </Button>

                        {/* Signed TDS/Manual — orange */}
                        <Button
                          size="sm"
                          variant=""
                          style={{ border: "1px solid #fd7e14", color: "#fd7e14", backgroundColor: "transparent" }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#fd7e14"; e.currentTarget.style.color = "#fff"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#fd7e14"; }}
                          onClick={() => {
                            const projId = wo.info?.project?._id || (typeof wo.info?.project === "string" ? wo.info?.project : "") || "";
                            navigate(`/tds?project=${projId}&workOrder=${wo.workOrder}`);
                          }}
                        >
                          Signed TDS/Manual
                        </Button>

                        {/* Proforma Invoice — pink */}
                        <Button
                          size="sm"
                          variant=""
                          style={{ border: "1px solid #e83e8c", color: "#e83e8c", backgroundColor: "transparent" }}
                          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#e83e8c"; e.currentTarget.style.color = "#fff"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#e83e8c"; }}
                          onClick={() => navigate(`/billings?workOrder=${wo.workOrder}`)}
                        >
                          Proforma Invoice
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            );
          })}
            </Row>
          )}

          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
              <Pagination className="mb-0">
                <Pagination.Prev
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                />
                {paginationItems}
                <Pagination.Next
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                />
              </Pagination>
              <span className="text-muted small">
                Page {currentPage} of {totalPages}
              </span>
            </div>
          )}
        </>
      )}

      <EmailModal
        show={showEmailModal}
        onHide={() => setShowEmailModal(false)}
        initialData={emailInitialData}
      />
    </>
  );
};

export default Billing;
